import React, { useState } from 'react';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Button from 'Components/UI/Button';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import useManagedAccountPageEventLogger
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import {
  FDAlert, FDBodyText, FDClickableText, FDHeadlineText,
} from '@facet-wealth/design';
import { Tooltip } from 'Components/UI/Tooltip';
import ManagedAccountAllocationPieChart from './ManagedAccountAllocationPieChart';
import ManagedAccountPortfolioBarChart from './ManagedAccountPortfolioBarChart';
import ManagedAccountPortfolioPieChart from './ManagedAccountPortfolioPieChart';
import ManagedAccountPortfolioCategories from './ManagedAccountPortfolioCategories';
import useManagedAccountPortfolio from './useManagedAccountPortfolio';

const useStyles = buildUseStyles('PortfolioSection', (theme) => ({
  root: {
    display: 'flex',
    flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
    gap: theme.spacing(3),
  },
  moduleContent: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
    flex: 1,
    gap: theme.spacing(3),
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  moduleChart: {
    display: 'flex',
    justifyContent: 'center',
    width: ({ isMobile }) => (isMobile ? '100%' : 'auto'),
    maxWidth: theme.dimensions.md,
    minHeight: ({ isMobile }) => (isMobile ? 'auto' : 250),
    aspectRatio: ({ isMobile }) => (isMobile ? 'auto' : 1),
    flexShrink: 1,
  },
  holdingsInfo: {
    display: 'grid',
    gridTemplateRows: ({ areHoldingsVisible }) => (areHoldingsVisible ? '1fr' : '0fr'),
    transition: 'grid-template-rows 0.5s ease-out',
    minWidth: theme.dimensions.xs,
    maxWidth: ({ isMobile }) => (isMobile ? 'auto' : theme.dimensions.sm),
  },
  holdingsInfoInner: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  moduleInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: theme.dimensions.xs,
    maxWidth: ({ isMobile }) => (isMobile ? 'auto' : theme.dimensions.sm),
    gap: theme.spacing(1),
    flexGrow: 1,
  },
  alert: {
    textAlign: 'start',
  },
}));

export default function PortfolioSection({
  managedAccount,
  performanceAllocationTitle,
  riskToleranceDetail,
  allocationModelDescription,
  portfolio,
  modelChangeEventsPageLink,
  hasLegacyAssets,
  legacyAssetsDescription,
}) {
  const isMobile = useMobileLayout();
  const [showHoldings, setShowHoldings] = useState(!isMobile);
  const areHoldingsVisible = !isMobile || showHoldings;
  const classes = useStyles({ isMobile, areHoldingsVisible });

  const { loadModalCollectionByLink } = useModalContext();

  function showAllModelChanges() {
    loadModalCollectionByLink(modelChangeEventsPageLink.url());
  }

  const {
    classifications,
    tickerClassifications,
    tickerClassificationsGrouped,
  } = useManagedAccountPortfolio({
    portfolio,
  });

  const { logPortfolioList } = useManagedAccountPageEventLogger();

  function toggleShowHoldings() {
    logPortfolioList(!showHoldings ? 'Show' : 'Hide');
    setShowHoldings(!showHoldings);
  }

  const showLegacyAssetsAlert = hasLegacyAssets && !managedAccount.isTruthy('directIndexingIsActive');
  return <>
    <Box className={classes.root}>
      {performanceAllocationTitle ? <>
        <Box className={classes.moduleContent}>
          {!isMobile ? <>
            <Box className={classes.moduleChart}>
              <ManagedAccountAllocationPieChart managedAccount={managedAccount} />
            </Box>
          </> : null}
          <Box className={classes.moduleInfo}>
            <FDHeadlineText value={'Target Mix'} />
            <Typography>{allocationModelDescription}</Typography>
            {riskToleranceDetail ? (<FDBodyText value={riskToleranceDetail} color={'textDisabled'} size={'sm'} />) : null}
            {!!modelChangeEventsPageLink && (
            <FDClickableText appearance={'minor'} onClick={showAllModelChanges} label={'Your Allocation History'} />
            )}
            {showLegacyAssetsAlert ? (
              <Tooltip placement={'bottom'} title={legacyAssetsDescription}>
                <Box>
                  {!managedAccount.isTruthy('isDirectIndexingActive') ? (
                    <FDAlert
                      className={classes.alert}
                      status={'info'}
                      title={'Performance may be impacted by legacy investments'}
                    />
                  ) : null}
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </>
        : null}
      {tickerClassifications?.length > 0 && (
        <Box className={classes.moduleContent}>
          <Box className={classes.moduleChart}>
            {isMobile
              ? <ManagedAccountPortfolioBarChart allocationModelClassifications={classifications} simple={!areHoldingsVisible} />
              : <ManagedAccountPortfolioPieChart allocationModelClassifications={classifications} tickerClassifications={tickerClassificationsGrouped} />}
          </Box>
          <Box className={classes.holdingsInfo}>
            <Box className={classes.holdingsInfoInner}>
              <FDHeadlineText value={portfolio.attr('title')} />
              <Typography>{portfolio.attr('description')}</Typography>
              <ManagedAccountPortfolioCategories tickerClassifications={tickerClassifications} />
            </Box>
          </Box>
          {isMobile && (
            <Button variant="text" color="primary" onClick={toggleShowHoldings}>
              {showHoldings ? 'Learn Less' : 'Learn More'}
              {showHoldings ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Button>
          )}
        </Box>
      )}
    </Box>

  </>;
}
